/**
 * «Qué otorga esto» — el resumen que va AL LADO del asignador de roles.
 *
 * Existe por un motivo concreto: tildar una casilla llamada «Contable» no dice
 * nada sobre lo que esa persona va a poder hacer mañana. Este bloque muestra, en
 * el mismo golpe de vista, las áreas que abre y —sobre todo— lo irreversible:
 * qué va a poder eliminar. Es el mismo dato que la ficha del explorador, resumido.
 *
 * Acepta varios roles porque los permisos se SUMAN: un usuario con dos roles
 * puede lo que permite cualquiera de los dos. Mostrarlos por separado haría que
 * la suma la tenga que hacer la persona de cabeza, que es justo donde se cometen
 * los errores de seguridad.
 */

import { Badge } from '@/components/shell/primitives';
import type { CrudVerb, Role, RoleCapability } from '@/panel/contract';
import { VERBOS, capacidadesDe, concedeAlgo } from '@/panel/contract';
import { Etiqueta } from '@/panel/ui';
import { ETIQUETA_VERBO, etiquetaDeArea } from '@/panel/vocabulario';

interface ResumenArea {
  key: string;
  label: string;
  /** Cuántos modelos distintos toca. */
  cuantos: number;
  /** Verbos que aparecen al menos una vez en el área. */
  verbos: Set<CrudVerb>;
  /** Modelos que puede eliminar: es lo único irreversible, va aparte. */
  eliminables: string[];
}

/** Une las capacidades de varios roles: el permiso más amplio gana, como en el backend. */
function unir(roles: Role[]): Map<string, { capacidad: RoleCapability; area: string; areaLabel: string }> {
  const union = new Map<string, { capacidad: RoleCapability; area: string; areaLabel: string }>();
  for (const rol of roles) {
    for (const { area, capability } of capacidadesDe(rol)) {
      if (!concedeAlgo(capability)) continue;
      const existente = union.get(capability.model);
      if (existente === undefined) {
        union.set(capability.model, {
          capacidad: { ...capability },
          area: area.key,
          areaLabel: area.label,
        });
      } else {
        for (const verbo of VERBOS) {
          if (capability[verbo]) existente.capacidad[verbo] = true;
        }
        if (existente.capacidad.labelIsTechnical && !capability.labelIsTechnical) {
          existente.capacidad.label = capability.label;
          existente.capacidad.labelIsTechnical = false;
        }
      }
    }
  }
  return union;
}

function resumirPorArea(roles: Role[]): ResumenArea[] {
  const porArea = new Map<string, ResumenArea>();

  for (const { capacidad, area, areaLabel } of unir(roles).values()) {
    let entrada = porArea.get(area);
    if (entrada === undefined) {
      entrada = {
        key: area,
        label: etiquetaDeArea(area, areaLabel),
        cuantos: 0,
        verbos: new Set<CrudVerb>(),
        eliminables: [],
      };
      porArea.set(area, entrada);
    }
    entrada.cuantos += 1;
    for (const verbo of VERBOS) {
      if (capacidad[verbo]) entrada.verbos.add(verbo);
    }
    if (capacidad.delete) entrada.eliminables.push(capacidad.label);
  }

  return [...porArea.values()].sort((a, b) => a.label.localeCompare(b.label, 'es'));
}

export function ResumenOtorga({
  roles,
  titulo,
  vacio,
}: {
  roles: Role[];
  titulo: string;
  /** Texto cuando no hay ningún rol que resumir. */
  vacio: string;
}) {
  const areas = resumirPorArea(roles);
  const eliminables = areas.flatMap((area) => area.eliminables);

  return (
    <div>
      <Etiqueta as="h4">{titulo}</Etiqueta>

      {areas.length === 0 ? (
        <p className="text-xs text-fg-muted">{vacio}</p>
      ) : (
        <>
          <ul className="space-y-2">
            {areas.map((area) => (
              <li key={area.key} className="border-b border-line/60 pb-2 last:border-b-0 last:pb-0">
                <p className="text-sm font-medium">{area.label}</p>
                <p className="mt-0.5 text-xs text-fg-muted">
                  {area.cuantos} {area.cuantos === 1 ? 'cosa' : 'cosas'} ·{' '}
                  {VERBOS.filter((verbo) => area.verbos.has(verbo))
                    .map((verbo) => ETIQUETA_VERBO[verbo].toLowerCase())
                    .join(', ')}
                </p>
              </li>
            ))}
          </ul>

          {eliminables.length > 0 && (
            <p className="mt-3 flex flex-wrap items-center gap-1.5">
              <Badge tone="danger">Puede eliminar</Badge>
              <span className="text-xs text-fg-muted">{eliminables.join(', ')}</span>
            </p>
          )}
        </>
      )}
    </div>
  );
}
